import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTPageURLToSDKPageURL } from '@wix/sdk-runtime/transformations/page-url';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CategoriesQueryBuilder,
  Category,
  CategoryCreatedEnvelope,
  CategoryDeletedEnvelope,
  CategoryQuery,
  CategoryUpdatedEnvelope,
  CreateCategoryOptions,
  GetCategoryBySlugOptions,
  GetCategoryBySlugResponse,
  GetCategoryOptions,
  GetCategoryResponse,
  ListCategoriesOptions,
  ListCategoriesResponse,
  QueryCategoriesOptions,
  QueryCategoriesResponse,
  UpdateCategory,
  UpdateCategoryOptions,
  createCategory as universalCreateCategory,
  deleteCategory as universalDeleteCategory,
  getCategory as universalGetCategory,
  getCategoryBySlug as universalGetCategoryBySlug,
  listCategories as universalListCategories,
  queryCategories as universalQueryCategories,
  typedQueryCategories as universalTypedQueryCategories,
  updateCategory as universalUpdateCategory,
} from './blog-v3-category-categories.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/blog' };

export function createCategory(
  httpClient: HttpClient
): CreateCategorySignature {
  return (
    category: NonNullablePaths<Category, `label`, 2>,
    options?: CreateCategoryOptions
  ) =>
    universalCreateCategory(
      category,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCategorySignature {
  /**
   * Creates a category.
   * @param - Category info.
   * @returns Category info.
   */
  (
    category: NonNullablePaths<Category, `label`, 2>,
    options?: CreateCategoryOptions
  ): Promise<
    NonNullablePaths<
      Category,
      | `_id`
      | `label`
      | `postCount`
      | `title`
      | `slug`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`,
      6
    >
  >;
}

export function updateCategory(
  httpClient: HttpClient
): UpdateCategorySignature {
  return (
    _id: string,
    category: UpdateCategory,
    options?: UpdateCategoryOptions
  ) =>
    universalUpdateCategory(
      _id,
      category,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCategorySignature {
  /**
   * Updates a category.
   * @param - Category ID.
   * @returns Category info.
   */
  (
    _id: string,
    category: UpdateCategory,
    options?: UpdateCategoryOptions
  ): Promise<
    NonNullablePaths<
      Category,
      | `_id`
      | `label`
      | `postCount`
      | `title`
      | `slug`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`,
      6
    >
  >;
}

export function getCategory(httpClient: HttpClient): GetCategorySignature {
  return (categoryId: string, options?: GetCategoryOptions) =>
    universalGetCategory(
      categoryId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCategorySignature {
  /**
   * Gets a category with the specified ID.
   * @param - Category ID.
   * @param - Options specifying which fields to return.
   */
  (categoryId: string, options?: GetCategoryOptions): Promise<
    NonNullablePaths<
      GetCategoryResponse,
      | `category._id`
      | `category.label`
      | `category.postCount`
      | `category.title`
      | `category.slug`
      | `category.seoData.tags`
      | `category.seoData.tags.${number}.type`
      | `category.seoData.tags.${number}.children`
      | `category.seoData.tags.${number}.custom`
      | `category.seoData.tags.${number}.disabled`
      | `category.seoData.settings.preventAutoRedirect`
      | `category.seoData.settings.keywords`
      | `category.seoData.settings.keywords.${number}.term`
      | `category.seoData.settings.keywords.${number}.isMain`,
      7
    >
  >;
}

export function getCategoryBySlug(
  httpClient: HttpClient
): GetCategoryBySlugSignature {
  return (slug: string, options?: GetCategoryBySlugOptions) =>
    universalGetCategoryBySlug(
      slug,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCategoryBySlugSignature {
  /**
   * Gets a category with the specified slug.
   *
   * The slug is at the end of the URL of a specific category.
   * For example, if a category's URL is `https://example.com/blog/categories/famous-cats`, the slug is `famous-cats`.
   * The slug is a case-sensitive string that is derived from the category's label, unless specified otherwise.
   * @param - Slug of the category to retrieve.
   *
   * The end of a category's URL. For example, `'https:/example.com/blog/category/{my-category-slug}'`. Case sensitive and generally based on the category `label` if not specified.
   * @param - Options specifying which fields to return.
   */
  (slug: string, options?: GetCategoryBySlugOptions): Promise<
    NonNullablePaths<
      GetCategoryBySlugResponse,
      | `category._id`
      | `category.label`
      | `category.postCount`
      | `category.title`
      | `category.slug`
      | `category.seoData.tags`
      | `category.seoData.tags.${number}.type`
      | `category.seoData.tags.${number}.children`
      | `category.seoData.tags.${number}.custom`
      | `category.seoData.tags.${number}.disabled`
      | `category.seoData.settings.preventAutoRedirect`
      | `category.seoData.settings.keywords`
      | `category.seoData.settings.keywords.${number}.term`
      | `category.seoData.settings.keywords.${number}.isMain`,
      7
    >
  >;
}

export function listCategories(
  httpClient: HttpClient
): ListCategoriesSignature {
  return (options?: ListCategoriesOptions) =>
    universalListCategories(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListCategoriesSignature {
  /**
   * Retrieves a list of up to 100 categories per request.
   *
   * If you pass a language filter to the method, it returns a list of up to 100 categories in the requested language.
   *
   * The categoies are displayed in order of their `displayPosition`, starting with `0`. The `displayPosition` is the position in which the categories
   * are displayed in the Category Menu page. By default, categories get added to the bottom of the Category Menu page with a `displayPosition` of `-1`.
   *
   * List Categories runs with these defaults, which you can override:
   * - `paging.limit` is `50`.
   * - `paging.offset` is `0`.
   *
   * List Categories is sorted by `displayPosition` in descending order. This cannot be overridden.
   * @param - Filter and paging options.
   */
  (options?: ListCategoriesOptions): Promise<
    NonNullablePaths<
      ListCategoriesResponse,
      | `categories`
      | `categories.${number}._id`
      | `categories.${number}.label`
      | `categories.${number}.postCount`
      | `categories.${number}.title`
      | `categories.${number}.slug`
      | `categories.${number}.seoData.settings.preventAutoRedirect`
      | `metaData.count`
      | `metaData.offset`
      | `metaData.total`,
      6
    >
  >;
}

export function queryCategories(
  httpClient: HttpClient
): QueryCategoriesSignature {
  return (options?: QueryCategoriesOptions) =>
    universalQueryCategories(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryCategoriesSignature {
  /**
   * Creates a query to retrieve a list of categories.
   *
   *
   * The Query Categories method builds a query to retrieve a list of up to 100 categories per language, and returns a [`CategoriesQueryBuilder`](https://dev.wix.com/docs/sdk/backend-modules/blog/categories/categories-query-builder/ascending) object.
   *
   * The response contains the query definition, which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/blog/categories/categories-query-builder/find) method.
   *
   * You can refine the query by chaining `CategoriesQueryBuilder` methods to the query. `CategoriesQueryBuilder` methods enable you to sort, filter, and control the results that Query Categories returns. Any methods chained to the Query Categories method are applied in the order that they are called.
   *
   * Query Categories runs with these `CategoriesQueryBuilder` defaults, which you can override.
   * - [`limit(100)`](https://dev.wix.com/docs/sdk/backend-modules/blog/categories/categories-query-builder/limit)
   * - [`ascending(displayPosition)`](https://dev.wix.com/docs/sdk/backend-modules/blog/categories/categories-query-builder/ascending)
   *
   * The following `CategoriesQueryBuilder` methods are supported for Query Categories. For a full description of the `Categories` object, see the response for the [`items`](https://dev.wix.com/docs/sdk/backend-modules/blog/categories/categories-query-result/items) property in [`CategoriesQueryResult`](https://www.wix.com/velo/reference/wix-blog-backend/categories/categoriesqueryresult).
   * @param - Options specifying which fields to return.
   */
  (options?: QueryCategoriesOptions): CategoriesQueryBuilder;
}

export function typedQueryCategories(
  httpClient: HttpClient
): TypedQueryCategoriesSignature {
  return (query: CategoryQuery, options?: QueryCategoriesOptions) =>
    universalTypedQueryCategories(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryCategoriesSignature {
  /** @returns A `CategoriesQueryBuilder` object that contains the refined query. */
  (query: CategoryQuery, options?: QueryCategoriesOptions): Promise<
    NonNullablePaths<
      QueryCategoriesResponse,
      | `categories`
      | `categories.${number}._id`
      | `categories.${number}.label`
      | `categories.${number}.postCount`
      | `categories.${number}.title`
      | `categories.${number}.slug`
      | `categories.${number}.seoData.settings.preventAutoRedirect`,
      6
    >
  >;
}

export function deleteCategory(
  httpClient: HttpClient
): DeleteCategorySignature {
  return (categoryId: string) =>
    universalDeleteCategory(
      categoryId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCategorySignature {
  /**
   * Deletes a category.
   * @param - Category ID.
   */
  (categoryId: string): Promise<void>;
}

export const onCategoryCreated = EventDefinition(
  'wix.blog.v3.category_created',
  true,
  (event: CategoryCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'entity.url' }, { path: 'entity.translations.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.coverImage' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CategoryCreatedEnvelope>();
export const onCategoryDeleted = EventDefinition(
  'wix.blog.v3.category_deleted',
  true,
  (event: CategoryDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'undefined.url' },
            { path: 'undefined.translations.url' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'undefined.coverImage' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CategoryDeletedEnvelope>();
export const onCategoryUpdated = EventDefinition(
  'wix.blog.v3.category_updated',
  true,
  (event: CategoryUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'entity.url' }, { path: 'entity.translations.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.coverImage' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CategoryUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApplicationError,
  BaseEventMetadata,
  BlogPaging,
  BulkActionMetadata,
  BulkCategoryResult,
  BulkCreateCategoriesRequest,
  BulkCreateCategoriesResponse,
  BulkDeleteCategoryRequest,
  BulkDeleteCategoryResponse,
  BulkUpdateCategoriesRequest,
  BulkUpdateCategoriesResponse,
  CategoriesQueryBuilder,
  CategoriesQueryResult,
  Category,
  CategoryCreatedEnvelope,
  CategoryDeletedEnvelope,
  CategoryLanguageCount,
  CategoryQuerySpec,
  CategoryTranslation,
  CategoryUpdatedEnvelope,
  CreateCategoryOptions,
  CreateCategoryRequest,
  CreateCategoryResponse,
  CursorPaging,
  Cursors,
  DeleteCategoryRequest,
  DeleteCategoryResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  Field,
  FocalPoint,
  GetCategoriesCountByLanguageRequest,
  GetCategoriesCountByLanguageResponse,
  GetCategoryBySlugOptions,
  GetCategoryBySlugRequest,
  GetCategoryBySlugResponse,
  GetCategoryOptions,
  GetCategoryRequest,
  GetCategoryResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InitialCategoriesCopied,
  ItemMetadata,
  Keyword,
  ListCategoriesOptions,
  ListCategoriesRequest,
  ListCategoriesResponse,
  MaskedCategory,
  MessageEnvelope,
  MetaData,
  Paging,
  PagingMetadataV2,
  PlatformQuery,
  PlatformQueryPagingMethodOneOf,
  QueryCategoriesOptions,
  QueryCategoriesRequest,
  QueryCategoriesResponse,
  RestoreInfo,
  SeoSchema,
  Settings,
  SortOrder,
  Sorting,
  Tag,
  UpdateCategory,
  UpdateCategoryOptions,
  UpdateCategoryRequest,
  UpdateCategoryResponse,
  WebhookIdentityType,
  utils,
} from './blog-v3-category-categories.universal.js';
